import React, { useState, forwardRef, useRef, useEffect, useContext } from 'react'
import { useInView } from 'framer-motion'
import { usePage } from '@inertiajs/react'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'
import { useMediaAsset, type MediaObject } from '../utils/useMediaUrl'
import { ModuleContext } from './ModuleContext'
import { useTheme } from '../utils/ThemeContext'
import { cn } from './ui/utils'

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'lottie-player': any
    }
  }
}

export interface MediaRendererProps {
  url?: string | null | undefined
  image?: MediaObject | string | null | undefined
  /** Specific media version to load (e.g., 'thumb', 'small', 'medium', 'large') */
  sizeVariant?: string | null
  mimeType?: string | null
  alt?: string | null
  className?: string
  fetchPriority?: 'high' | 'low' | 'auto'
  decoding?: 'async' | 'auto' | 'sync'
  loading?: 'lazy' | 'eager'
  controls?: boolean
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  playsInline?: boolean
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  objectPosition?: 'left' | 'center' | 'right' | 'top' | 'bottom' | string
  playMode?: 'autoplay' | 'inline' | 'modal'
  poster?: string | null
  width?: string | number
  height?: string | number
  sizes?: string
}

export const MediaRenderer = forwardRef<HTMLImageElement | HTMLVideoElement, MediaRendererProps>(
  (
    {
      url: explicitUrl,
      image,
      sizeVariant,
      mimeType: explicitMimeType,
      alt = '',
      className = 'w-full h-full object-cover',
      fetchPriority,
      decoding,
      loading,
      controls: initialControls,
      autoPlay: initialAutoPlay,
      loop: initialLoop,
      muted: initialMuted,
      playsInline = true,
      objectFit = 'cover',
      objectPosition = 'center',
      playMode = 'autoplay',
      poster,
      width,
      height,
      sizes,
    },
    ref
  ) => {
    const internalRef = useRef<any>(null)
    const combinedRef = (ref as any) || internalRef
    const [mounted, setMounted] = useState(false)
    const { isFirst } = useContext(ModuleContext)
    const observedInView = useInView(combinedRef, {
      once: true,
      margin: '0px 0px -100px 0px',
    })
    // The first module is above the fold, so treat it as visible without waiting
    // on the observer.
    const isInView = isFirst || observedInView
    const [isModalOpen, setIsModalOpen] = useState(false)

    useEffect(() => {
      setMounted(true)
    }, [])
    const page = usePage()
    const { isDark } = useTheme()
    const lottieEnabled = (page.props.features as any)?.lottie !== false

    // Performance optimizations for LCP (First module's images)
    const effectiveFetchPriority = fetchPriority || (isFirst ? 'high' : undefined)
    const effectiveLoading = loading || (isFirst ? 'eager' : 'lazy')
    const effectiveDecoding = decoding || (isFirst ? 'sync' : 'async')

    // Resolve URL seamlessly if an image object is provided, otherwise fall back to explicit url
    const asset = useMediaAsset(image, sizeVariant || 'large')

    /**
     * The other scheme's version of the same image, when there is one.
     *
     * Which scheme the page is in cannot be known on a first request: the
     * preference lives in the visitor's browser, the cookie that carries it to
     * the server is written by the page itself, and no request before that one
     * has it. So the server renders the site default and the browser, which
     * read `prefers-color-scheme` before React started, renders the other —
     * two different `src` values for the same `img`, which is a hydration
     * mismatch React reports and explicitly does not repair.
     *
     * Resolving both and letting the `dark:` classes below pick is what takes
     * the decision out of render entirely. The markup is then the same
     * whatever the server guessed, so there is nothing to mismatch, and the
     * right image is painted on the first frame instead of appearing once
     * JavaScript has run.
     */
    const counterpart = useMediaAsset(image, sizeVariant || 'large', !isDark)
    const resolvedUrl = asset.url || explicitUrl
    const intrinsicWidth = asset.width
    const intrinsicHeight = asset.height
    const srcset = asset.srcset

    // Respect focal point if provided in metadata and no explicit position is set
    let effectiveObjectPosition = objectPosition
    if (asset.focalPoint && (objectPosition === 'center' || !objectPosition)) {
      const { x, y } = asset.focalPoint
      effectiveObjectPosition = `${x * 100}% ${y * 100}%`
    }

    // FAILSAVE: Manually sync src/srcset after hydration if they mismatch.
    // This handles aggressive browser caching where React's attribute update 
    // isn't always enough to force a swap of an already-loaded responsive image.
    useEffect(() => {
      if (typeof document !== 'undefined' && combinedRef.current instanceof HTMLImageElement) {
        const img = combinedRef.current
        if (resolvedUrl && img.src !== resolvedUrl && !resolvedUrl.startsWith('data:')) {
          img.src = resolvedUrl
        }
        if (srcset && img.srcset !== srcset) {
          img.srcset = srcset
        }
      }
    }, [resolvedUrl, srcset, isDark])


    const mimeType =
      (typeof image === 'object' && image !== null ? (image as any).mimeType : null) ||
      explicitMimeType

    if (!resolvedUrl || typeof resolvedUrl !== 'string') {
      if (resolvedUrl) {
        console.warn('[MediaRenderer] resolvedUrl is not a string:', resolvedUrl, {
          image,
          explicitUrl,
        })
      }
      return null
    }

    const isVideo =
      (typeof mimeType === 'string' && mimeType.startsWith('video/')) ||
      /\.(mp4|webm|ogg|mov|m4v|avi)$/i.test(resolvedUrl.split('?')[0].toLowerCase())

    const isAutoplayMode = playMode === 'autoplay'
    const effectiveInView = mounted && isInView

    useEffect(() => {
      if (isVideo && isAutoplayMode && effectiveInView && combinedRef.current) {
        // Explicitly set muted and loop properties to ensure autoplay is allowed by browser policies
        combinedRef.current.muted = true
        combinedRef.current.loop = true
        combinedRef.current.play().catch((err: any) => {
          // Fallback or ignore: browsers might still block it if no user interaction
          console.warn('[MediaRenderer] Autoplay failed:', err)
        })
      }
    }, [isInView, isVideo, isAutoplayMode])

    const isLottie =
      (typeof mimeType === 'string' &&
        (mimeType === 'application/json' || mimeType === 'application/x-lottie')) ||
      /\.(json|lottie)$/i.test(resolvedUrl.split('?')[0].toLowerCase())

    const isSvg =
      (typeof mimeType === 'string' && mimeType === 'image/svg+xml') ||
      /\.svg$/i.test(resolvedUrl.split('?')[0].toLowerCase())

    if (isVideo || (isLottie && lottieEnabled) || (isSvg && playMode === 'modal')) {
      const isModalMode = playMode === 'modal'

      if (isModalMode) {
        return (
          <>
            <div
              className="relative group cursor-pointer w-full h-full"
              onClick={() => setIsModalOpen(true)}
            >
              {isVideo || isSvg ? (
                isVideo ? (
                  <video
                    src={resolvedUrl}
                    className={className}
                    style={{ objectFit, objectPosition: effectiveObjectPosition }}
                    playsInline={playsInline}
                    autoPlay={false}
                    loop={false}
                    muted={true}
                    controls={false}
                    poster={poster || undefined}
                    ref={ref as React.Ref<HTMLVideoElement>}
                    width={width ?? (intrinsicWidth ? Number(intrinsicWidth) : undefined)}
                    height={height ?? (intrinsicHeight ? Number(intrinsicHeight) : undefined)}
                    // @ts-ignore
                    fetchPriority={effectiveFetchPriority}
                  />
                ) : (
                  <img
                    src={resolvedUrl}
                    srcSet={!isSvg && !isVideo ? srcset : undefined}
                    sizes={!isSvg && !isVideo ? (sizes || (isFirst ? '100vw' : undefined)) : undefined}
                    alt={alt || (typeof image === 'object' ? ((image as any)?.altText || (image as any)?.alt) : null) || ''}
                    className={className}
                    style={{ objectFit, objectPosition: effectiveObjectPosition }}
                    width={width ?? (intrinsicWidth ? Number(intrinsicWidth) : undefined)}
                    height={height ?? (intrinsicHeight ? Number(intrinsicHeight) : undefined)}
                    fetchPriority={effectiveFetchPriority}
                    loading={effectiveLoading}
                    decoding={effectiveDecoding}
                  />
                )
              ) : (
                <div className={className} style={{ objectFit, objectPosition: effectiveObjectPosition }}>
                  {lottieEnabled && (
                    // @ts-ignore
                    <lottie-player
                      src={resolvedUrl}
                      autoplay={false}
                      loop={false}
                      style={{ width: '100%', height: '100%' }}
                    />
                  )}
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80 text-black shadow-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogContent
                className="max-w-4xl p-0 overflow-hidden bg-black border-none"
                aria-describedby={undefined}
              >
                <DialogTitle className="sr-only">
                  {isVideo ? 'Video Player' : 'Animation Player'}
                </DialogTitle>
                {isVideo ? (
                  <video
                    src={resolvedUrl}
                    className="w-full h-auto max-h-[80vh]"
                    controls
                    autoPlay
                    playsInline
                  />
                ) : isSvg ? (
                  <div className="w-full h-auto max-h-[80vh] flex items-center justify-center bg-white/5 p-8">
                    <img
                      src={resolvedUrl}
                      srcSet={!isSvg ? srcset : undefined}
                      sizes="90vw"
                      alt={alt || (typeof image === 'object' ? ((image as any)?.altText || (image as any)?.alt) : null) || ''}
                      className="max-w-full max-h-full"
                      width={intrinsicWidth ? Number(intrinsicWidth) : undefined}
                      height={intrinsicHeight ? Number(intrinsicHeight) : undefined}
                    />
                  </div>
                ) : (
                  <div className="w-full h-auto max-h-[80vh] flex items-center justify-center bg-white/5">
                    {lottieEnabled && (
                      // @ts-ignore
                      <lottie-player
                        src={resolvedUrl}
                        autoplay
                        loop
                        controls
                        style={{ width: '100%', height: '100%', maxWidth: '800px' }}
                      />
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )
      }

      if (isVideo) {
        // A looping video is ambient, never something to listen to, so it is
        // silent whatever the caller passed or the visitor clicked. React does
        // not reliably reflect `muted` onto the element, hence the handlers.
        const loops = initialLoop ?? isAutoplayMode
        const keepSilent = loops
          ? (e: React.SyntheticEvent<HTMLVideoElement>) => {
              if (!e.currentTarget.muted) e.currentTarget.muted = true
            }
          : undefined
        return (
          <video
            src={resolvedUrl}
            className={className}
            style={{ objectFit, objectPosition: effectiveObjectPosition }}
            playsInline={playsInline}
            ref={combinedRef}
            controls={initialControls ?? (isAutoplayMode ? false : true)}
            autoPlay={initialAutoPlay ?? (isAutoplayMode ? (effectiveInView ? true : false) : false)}
            loop={loops}
            muted={loops || (initialMuted ?? isAutoplayMode)}
            onPlay={keepSilent}
            onVolumeChange={keepSilent}
            poster={poster || undefined}
                    width={width ?? (intrinsicWidth ? Number(intrinsicWidth) : undefined)}
                    height={height ?? (intrinsicHeight ? Number(intrinsicHeight) : undefined)}
            // @ts-ignore
            fetchPriority={effectiveFetchPriority}
          />
        )
      } else if (isLottie && lottieEnabled) {
        return (
          // @ts-ignore
          <lottie-player
            src={resolvedUrl}
            autoplay={initialAutoPlay ?? (isAutoplayMode ? (isInView ? true : false) : false)}
            loop={initialLoop ?? (isAutoplayMode ? true : false)}
            controls={initialControls ?? (isAutoplayMode ? false : true)}
            style={{
              width: width || (intrinsicWidth ? Number(intrinsicWidth) : '100%'),
              height: height || (intrinsicHeight ? Number(intrinsicHeight) : '100%'),
              objectFit,
              objectPosition: effectiveObjectPosition,
            }}
            className={className}
          />
        )
      }
    }

    const imageProps = {
      srcSet: !isSvg && !isVideo ? srcset : undefined,
      sizes: !isSvg && !isVideo ? sizes || (isFirst ? '100vw' : undefined) : undefined,
      alt: alt || (typeof image === 'object' ? ((image as any)?.altText || (image as any)?.alt) : null) || '',
      width: width ?? (intrinsicWidth ? Number(intrinsicWidth) : undefined),
      height: height ?? (intrinsicHeight ? Number(intrinsicHeight) : undefined),
      fetchPriority: effectiveFetchPriority,
      decoding: effectiveDecoding,
      loading: effectiveLoading,
      style: {
        objectFit,
        objectPosition: effectiveObjectPosition,
        ...(width && height ? { aspectRatio: `${width}/${height}` } : {}),
        ...((!width || !height) && intrinsicWidth && intrinsicHeight
          ? { aspectRatio: `${intrinsicWidth}/${intrinsicHeight}` }
          : {}),
      },
    }

    /*
     * An image with a version for each scheme renders both, shown by CSS.
     *
     * `.dark` is on `<html>` before the first paint, so the classes decide
     * this earlier than any render could, and identically on server and
     * client. Both files are fetched, which is why this is limited to images
     * that actually have a counterpart — a deliberately authored pair, in
     * practice a logo or a diagram, not a photograph.
     */
    const lightUrl = isDark ? counterpart.url : resolvedUrl
    const darkUrl = isDark ? resolvedUrl : counterpart.url

    if (lightUrl && darkUrl && lightUrl !== darkUrl) {
      /*
       * Neither half is given a priority hint.
       *
       * React turns `fetchPriority="high"` into a `<link rel="preload">` while
       * rendering on the server, and here that would preload both files when
       * one of them is certain to be hidden — a wasted download the browser
       * then warns about. Which one is wasted depends on the visitor's scheme,
       * which is the thing this whole branch exists because the server does not
       * know, so there is no half to favour. They stay eager, so the visible
       * one still paints without waiting for anything; only the guess about
       * which to fetch first is given up, and a paired image is a logo or a
       * diagram rather than a photograph.
       */
      const paired = { ...imageProps, fetchPriority: undefined }

      return (
        <>
          <img ref={combinedRef} src={lightUrl} {...paired} className={cn(className, 'dark:hidden')} />
          <img src={darkUrl} {...paired} className={cn(className, 'hidden dark:block')} />
        </>
      )
    }

    return <img ref={combinedRef} src={resolvedUrl} {...imageProps} className={className} />
  }
)

MediaRenderer.displayName = 'MediaRenderer'
